import { Formio } from "formiojs";

const FieldComponent = Formio.Components.components.field;

function escapeHtml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function setByPath(object, path, value) {
  if (!object || !path) return;
  const parts = String(path).split(".").filter(Boolean);
  let current = object;
  parts.forEach((part, index) => {
    if (index === parts.length - 1) current[part] = value;
    else {
      if (!current[part] || typeof current[part] !== "object") current[part] = {};
      current = current[part];
    }
  });
}

export default class BundIdAuthenticationComponent extends FieldComponent {
  static schema(...extend) {
    return FieldComponent.schema({
      type: "bundIdAuthentication",
      label: "BundID / Online-Ausweis",
      key: "bundIdAuthentication",
      input: true,
      tableView: false,
      persistent: false,
      clearOnHide: false,
      buttonText: "Mit Online-Ausweis identifizieren",
      successFlagKey: "isVerified",
      providerTargetKey: "authenticationProvider",
      methodTargetKey: "authenticationMethod",
      trustLevelTargetKey: "authenticationTrustLevel",
      authenticatedAtTargetKey: "authenticatedAtUtc",
      showDetails: true
    }, ...extend);
  }

  static get builderInfo() {
    return {
      title: "BundID / Online-Ausweis",
      icon: "id-card",
      group: "basic",
      weight: 30,
      schema: BundIdAuthenticationComponent.schema()
    };
  }

  static editForm() {
    return {
      components: [{
        type: "tabs",
        key: "tabs",
        components: [
          {
            key: "display",
            label: "Anzeige",
            components: [
              { type: "textfield", key: "label", label: "Bezeichnung" },
              { type: "textfield", key: "key", label: "Key", validate: { required: true } },
              { type: "textfield", key: "buttonText", label: "Text der Schaltfläche" },
              { type: "checkbox", key: "showDetails", label: "Authentifizierungsdetails anzeigen", defaultValue: true }
            ]
          },
          {
            key: "data",
            label: "Zielwerte",
            components: [
              { type: "textfield", key: "successFlagKey", label: "Ziel-Key: erfolgreich verifiziert" },
              { type: "textfield", key: "providerTargetKey", label: "Ziel-Key: Provider" },
              { type: "textfield", key: "methodTargetKey", label: "Ziel-Key: Methode" },
              { type: "textfield", key: "trustLevelTargetKey", label: "Ziel-Key: Vertrauensniveau" },
              { type: "textfield", key: "authenticatedAtTargetKey", label: "Ziel-Key: Zeitpunkt" },
              {
                type: "htmlelement",
                tag: "div",
                content: "Hinweis: Diese Form.io-Werte dienen nur der UI. Das Backend prüft bei Formularen mit Sicherheitsniveau Hoch die form_sessions-Tabelle erneut."
              }
            ]
          }
        ]
      }]
    };
  }

  constructor(component, options, data) {
    super(component, options, data);
    this._status = null;
    this._statusLoaded = false;
    this._loading = false;
    this._starting = false;
    this._error = null;
  }

  render() {
    const status = this._status || {};
    const authenticated = status.authenticated === true;
    const statusText = authenticated
      ? "Identität erfolgreich bestätigt"
      : (this._loading ? "Authentifizierungsstatus wird geprüft ..." : "Noch nicht authentifiziert");

    const details = authenticated && this.component.showDetails !== false
      ? `<div class="small text-muted mt-2">
           <div><strong>Verfahren:</strong> ${escapeHtml(status.method || "eID")}</div>
           <div><strong>Vertrauensniveau:</strong> ${escapeHtml(status.trustLevel || "STORK-QAA-Level-4")}</div>
         </div>`
      : "";

    const error = this._error
      ? `<div class="alert alert-danger mt-3 mb-0" ref="errorRef">${escapeHtml(this._error)}</div>`
      : "";

    return super.render(`
      <div class="card border-light">
        <div class="card-body px-0 py-2">
          <div class="mb-2" ref="statusRef">
            <strong>${authenticated ? "✓ " : ""}${escapeHtml(statusText)}</strong>
          </div>
          ${details}
          <button
            type="button"
            class="btn btn-primary mt-3"
            ref="startRef"
            ${this._starting ? "disabled" : ""}
          >
            ${escapeHtml(this._starting ? "Weiterleitung zu BundID ..." : (this.component.buttonText || "Mit Online-Ausweis identifizieren"))}
          </button>
          ${error}
        </div>
      </div>
    `);
  }

  attach(element) {
    const attached = super.attach(element);
    this.loadRefs(element, { startRef: "single", statusRef: "single", errorRef: "single" });

    if (this.refs.startRef) {
      this.addEventListener(this.refs.startRef, "click", () => this.startAuthentication());
    }

    if (!this._statusLoaded && !this._loading) {
      window.setTimeout(() => this.refreshStatus(), 0);
    }

    return attached;
  }

  getAxios() {
    const axios = window.GLOBALAXIOS;
    if (!axios) throw new Error("GLOBALAXIOS ist nicht verfügbar.");
    const token = localStorage.getItem("ozgJwt");
    if (token) axios.defaults.headers.common["Authorization"] = "Bearer " + token;
    return axios;
  }

  getApiUrl(path) {
    if (!window.GLOBALCONFIG || !window.GLOBALCONFIG.API_URL) {
      throw new Error("GLOBALCONFIG.API_URL ist nicht verfügbar.");
    }
    return String(window.GLOBALCONFIG.API_URL).replace(/\/+$/, "") + path;
  }

  async refreshStatus(force = false) {
    if (this._loading || (this._statusLoaded && !force)) return;
    this._loading = true;
    this._error = null;
    try {
      const response = await this.getAxios().get(this.getApiUrl("/api/bundid/status"));
      this._status = response && response.data ? response.data : {};
      this._statusLoaded = true;
      this.dataValue = this._status;
      this.applyStatusToSubmission(this._status);
    } catch (error) {
      this._error = error?.response?.data?.messageDe || error?.message || "Der BundID-Status konnte nicht geladen werden.";
    } finally {
      this._loading = false;
      this.redraw();
    }
  }

  applyStatusToSubmission(status) {
    const rootData = this.root?.submission?.data || this.data;
    if (!rootData) return;
    setByPath(rootData, this.component.successFlagKey, status?.authenticated === true);
    setByPath(rootData, this.component.providerTargetKey, status?.provider || null);
    setByPath(rootData, this.component.methodTargetKey, status?.method || null);
    setByPath(rootData, this.component.trustLevelTargetKey, status?.trustLevel || null);
    setByPath(rootData, this.component.authenticatedAtTargetKey, status?.authenticatedAtUtc || null);
    if (this.root && typeof this.root.triggerChange === "function") this.root.triggerChange();
  }

  cleanReturnUrl() {
    const url = new URL(window.location.href);
    ["bundid_auth", "bundid_error", "t", "token", "jwt", "access_token"].forEach((key) => url.searchParams.delete(key));
    return url.toString();
  }

  async startAuthentication() {
    if (this._starting) return;
    this._starting = true;
    this._error = null;
    this.redraw();
    try {
      const response = await this.getAxios().get(
        this.getApiUrl("/api/bundid/start"),
        { params: { returnUrl: this.cleanReturnUrl() } }
      );
      const payload = response?.data || {};
      if (!payload.success || !payload.destination || !payload.samlRequest || !payload.relayState) {
        throw new Error(payload.messageDe || "BundID hat keine vollständigen Startdaten geliefert.");
      }

      const destination = new URL(payload.destination);
      if (destination.protocol !== "https:") throw new Error("Ungültige BundID-Zieladresse.");

      const form = document.createElement("form");
      form.method = "POST";
      form.action = destination.toString();
      form.style.display = "none";

      const samlInput = document.createElement("input");
      samlInput.type = "hidden";
      samlInput.name = "SAMLRequest";
      samlInput.value = payload.samlRequest;
      form.appendChild(samlInput);

      const relayInput = document.createElement("input");
      relayInput.type = "hidden";
      relayInput.name = "RelayState";
      relayInput.value = payload.relayState;
      form.appendChild(relayInput);

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      this._starting = false;
      this._error = error?.response?.data?.messageDe || error?.message || "Die BundID-Authentifizierung konnte nicht gestartet werden.";
      this.redraw();
    }
  }
}

Formio.Components.addComponent("bundIdAuthentication", BundIdAuthenticationComponent);
